import { useEffect, useRef, useState } from 'react';
import { Input } from '@react-apps/design-system';
import { useContactSearch } from './use-contact-search';
import {
  recallContactLabel,
  rememberContactLabel,
} from './contact-label-cache';
import type { ContactOption } from './contacts-api.datasource';

interface ContactSearchInputProps {
  label?: string;
  /**
   * O `userId` escolhido — e só ele. O rótulo legível não é estado do filtro:
   * sai do `contact-label-cache`, justamente para não acompanhar o filtro até
   * a URL (ver o comentário lá).
   */
  value: string | null;
  onChange: (userId: string | null) => void;
  /**
   * Texto digitado. Controlado de fora de propósito: este componente é usado
   * dentro de `Modal`, que desmonta os filhos ao fechar (`if (!open) return
   * null`) — estado interno aqui seria perdido a cada fechamento, enquanto os
   * outros campos do filtro sobrevivem no `draft` do modal.
   */
  term: string;
  onTermChange: (term: string) => void;
}

function optionLabel(option: ContactOption): string {
  return option.email ? `${option.name} — ${option.email}` : option.name;
}

/**
 * Autocomplete de contato compartilhado pelos filtros de Vendas e Contratos.
 * Busca no Identity por nome ou e-mail parcial e devolve um `userId` — é ele que
 * vai como query param, então o filtro vale para a base toda, não só para a
 * página carregada.
 *
 * O rótulo do contato já escolhido vem do `contact-label-cache` (sessionStorage),
 * não de quem chama: é o que mantém nome e e-mail fora da URL. Sem entrada no
 * cache o campo mostra o próprio `userId` — o filtro segue valendo.
 *
 * Sem seleção não há filtro: de propósito não existe fallback por texto livre,
 * que era justamente o comportamento fraco que este componente substitui. Como
 * digitar não filtra nada por si, o campo avisa explicitamente enquanto houver
 * texto sem contato escolhido — senão o filtro parece aplicado (a busca do
 * autocomplete acontece na hora) e a listagem não muda.
 *
 * A lista é montada à mão em vez de usar o `Dropdown` do design-system porque
 * aquele controla `open` internamente e só abre no clique do trigger — aqui ela
 * precisa abrir enquanto se digita.
 */
export function ContactSearchInput({
  label = 'Contato',
  value,
  onChange,
  term,
  onTermChange,
}: ContactSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { options, isLoading, isTermTooShort, isEmpty } =
    useContactSearch(term);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const select = (option: ContactOption) => {
    rememberContactLabel(option.userId, optionLabel(option));
    onChange(option.userId);
    onTermChange('');
    setIsOpen(false);
  };

  const clear = () => {
    onChange(null);
    onTermChange('');
    setIsOpen(false);
  };

  if (value) {
    return (
      <div className="space-y-1.5">
        <span className="text-(--color-text-secondary) block text-sm font-medium">
          {label}
        </span>
        <div className="border-(--color-border) bg-(--color-bg-secondary) flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
          <span className="text-(--color-text-primary) truncate text-sm">
            {recallContactLabel(value) ?? value}
          </span>
          <button
            type="button"
            onClick={clear}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) shrink-0 text-xs underline"
          >
            Trocar
          </button>
        </div>
      </div>
    );
  }

  const showPanel =
    isOpen && (isLoading || isTermTooShort || isEmpty || options.length > 0);

  // Digitou algo mas não escolheu ninguém: o filtro não vale. Sem este aviso a
  // busca do autocomplete dá a impressão de que já filtrou.
  const needsSelection = term.trim().length > 0 && !isTermTooShort;

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        id="contact-search"
        autoComplete="off"
        placeholder="Buscar por nome ou e-mail..."
        value={term}
        onChange={(e) => {
          onTermChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        helperText={
          needsSelection
            ? 'Escolha um contato da lista — só assim o filtro é aplicado.'
            : undefined
        }
      />

      {showPanel && (
        <div className="border-(--color-border) bg-(--color-bg-primary) absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border shadow-lg">
          {isTermTooShort ? (
            <p className="text-(--color-text-secondary) px-3 py-2 text-xs">
              Digite ao menos 2 caracteres.
            </p>
          ) : isLoading ? (
            <p className="text-(--color-text-secondary) px-3 py-2 text-xs">
              Buscando…
            </p>
          ) : isEmpty ? (
            <p className="text-(--color-text-secondary) px-3 py-2 text-xs">
              Nenhum contato encontrado.
            </p>
          ) : (
            <ul>
              {options.map((option) => (
                <li key={option.userId}>
                  <button
                    type="button"
                    onClick={() => select(option)}
                    className="text-(--color-text-primary) hover:bg-(--color-bg-secondary) block w-full px-3 py-2 text-left text-sm transition-colors"
                  >
                    <span className="block truncate">{option.name}</span>
                    {option.email && (
                      <span className="text-(--color-text-secondary) block truncate text-xs">
                        {option.email}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
